import { ALGORITHM_IDS } from '~/models/schemaVersion'
import type { Centimetres, Percent } from '~/types/units'
import { centimetresToInches, unwrapNumber } from '~/types/units'

/**
 * `male_navy_circumference_v1` — the "Navy circumference estimate for men"
 * (D-014, measurement-protocol.md).
 *
 * Canonical centimetres are converted to inches first, then:
 *
 *   body_fat_percent = 86.010 × log10(waist_in − neck_in)
 *                    − 70.041 × log10(height_in)
 *                    + 36.76
 *
 * Version 1 supports no women's formula, no sex selector, and no alternative
 * variant (D-013).
 *
 * This is a longitudinal personal-tracking estimate. It is NOT an official
 * military compliance result, a clinical assessment, or an exact body-fat
 * measurement. Version 1 calculates and displays no numerical practical range
 * (D-015).
 *
 * Pure: no clock, no storage, no rounding. Full precision is returned and the
 * presentation layer rounds (D-041).
 */

const WAIST_COEFFICIENT = 86.01
const HEIGHT_COEFFICIENT = 70.041
const CONSTANT = 36.76

export type NavyInvalidReason =
  | 'missingHeight'
  | 'missingWaist'
  | 'missingNeck'
  | 'nonFiniteInput'
  | 'nonPositiveInput'
  /** `waist − neck` must be strictly positive before the logarithm is taken. */
  | 'waistNotGreaterThanNeck'

export interface NavyInputs {
  readonly heightCm: Centimetres | undefined
  readonly waistAtNavelCm: Centimetres | undefined
  readonly neckCm: Centimetres | undefined
}

/**
 * The result.
 *
 * `implausible` deliberately CARRIES the computed value rather than replacing
 * or clamping it: measurement-protocol.md requires that implausible output be
 * surfaced as a data-quality state with the source readings preserved, never
 * silently pinned to a boundary.
 */
export type NavyEstimate =
  | {
      readonly status: 'ok'
      readonly bodyFatPercent: Percent
      readonly algorithm: typeof ALGORITHM_IDS.bodyFat
    }
  | {
      readonly status: 'implausible'
      readonly bodyFatPercent: Percent
      readonly algorithm: typeof ALGORITHM_IDS.bodyFat
    }
  | { readonly status: 'invalid'; readonly reason: NavyInvalidReason }

/**
 * Implausibility is judged ARITHMETICALLY, not physiologically.
 *
 * A result at or below 0%, or at or above 100%, is not a possible percentage of
 * body mass, so flagging it invents no policy. A narrower physiological band
 * (say "below 3% is unrealistic") would be a clinical judgment and is
 * deliberately NOT invented here — it would need its own decision.
 */
function isPlausiblePercentage(value: number): boolean {
  return value > 0 && value < 100
}

export function estimateNavyBodyFat(inputs: NavyInputs): NavyEstimate {
  const { heightCm, waistAtNavelCm, neckCm } = inputs

  if (heightCm === undefined) return { status: 'invalid', reason: 'missingHeight' }
  if (waistAtNavelCm === undefined) return { status: 'invalid', reason: 'missingWaist' }
  if (neckCm === undefined) return { status: 'invalid', reason: 'missingNeck' }

  const values = [unwrapNumber(heightCm), unwrapNumber(waistAtNavelCm), unwrapNumber(neckCm)]
  if (values.some((value) => !Number.isFinite(value))) {
    return { status: 'invalid', reason: 'nonFiniteInput' }
  }
  if (values.some((value) => value <= 0)) {
    return { status: 'invalid', reason: 'nonPositiveInput' }
  }

  const waistInches = unwrapNumber(centimetresToInches(waistAtNavelCm))
  const neckInches = unwrapNumber(centimetresToInches(neckCm))
  const heightInches = unwrapNumber(centimetresToInches(heightCm))

  const girth = waistInches - neckInches
  if (girth <= 0) {
    // log10 of zero or a negative is not a number. Refuse rather than emit NaN.
    return { status: 'invalid', reason: 'waistNotGreaterThanNeck' }
  }

  const bodyFat =
    WAIST_COEFFICIENT * Math.log10(girth) - HEIGHT_COEFFICIENT * Math.log10(heightInches) + CONSTANT

  return {
    status: isPlausiblePercentage(bodyFat) ? 'ok' : 'implausible',
    bodyFatPercent: bodyFat as Percent,
    algorithm: ALGORITHM_IDS.bodyFat,
  }
}

/** Whether an estimate carries a usable number, plausible or not. */
export function hasEstimateValue(
  estimate: NavyEstimate,
): estimate is Extract<NavyEstimate, { bodyFatPercent: Percent }> {
  return estimate.status === 'ok' || estimate.status === 'implausible'
}
